import {action, classNames, Fragment, observable, observer, _, React, useAsObservableSource} from '@api'
import {Button, Icon, Menu, MenuDivider, MenuItem} from '@blueprintjs/core'
import {Popover2} from '@api'
import {FlexLayoutNode, TabNode} from '@flexlayout'
import {ContextMenuItem, NewFlexTabMenuItems, useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {StoresInstance} from '@stores'
import {VipFlexLayoutTabStore, VipFlexLayoutTabStoreContext, MyFlexLayoutTabContentLeading, NewFlexTabButton, useVipFlexLayoutTabStore} from '@stores/catalog/flexLayout'
import {PrimaryIconButton} from 'centric-ui-components'
import * as css from './FlexLayoutReportNavigator.css'

/// region Store

export class FlexLayoutReportNavigatorStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
    stores.flexLayout.navigator = this
  }

  @observable foo = false

  @action goStart = () => {
    this.flexLayout.firstTab.selectInTabSet()
  }

  @action goPrevious = () => {
    this.flexLayout.previousTab.selectInTabSet()
  }

  @action goNext = () => {
    this.flexLayout.nextTab.selectInTabSet()
  }

  @action goEnd = () => {
    this.flexLayout.lastTab.selectInTabSet()
  }

  @observable isPopoverOpen = false

  @action onPopoverInteraction = (nextState) => {
    if (!nextState) {
      this.isPopoverOpen = nextState
    }
  }

  @action togglePopover = () => this.isPopoverOpen = !this.isPopoverOpen
  @action toggleMaximize = () => {
    const {flexLayout: {activeTab, toggleMaximize}} = this
    toggleMaximize(activeTab)
  }
}

export const FlexLayoutReportNavigatorStoreContext = React.createContext<FlexLayoutReportNavigatorStore>(null)
export const useFlexLayoutReportNavigatorStore = () => React.useContext(FlexLayoutReportNavigatorStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const FlexLayoutReportNavigator = observer<MyProps>(function FlexLayoutReportNavigator({className, ...props}) {
  const store = useVipLocalStore((stores) => new FlexLayoutReportNavigatorStore(stores)),
    {
      goPrevious, goNext, goEnd, goStart, isPopoverOpen, onPopoverInteraction, togglePopover, toggleMaximize,
      flexLayout: {flexEnabled, tabsInOrder, activeTab}
    } = store,
    tabMenuStoreProps = useAsObservableSource({flexNode: activeTab}),
    tabMenuStore = useVipLocalStore(s => new VipFlexLayoutTabStore(s, tabMenuStoreProps))

  if (true) {
    return null // NY Ready for General Use - move functionality to menu or sidebar most likely
  } else {
    const {catalogRoot} = activeTab ?? {},
      count = !catalogRoot ? undefined : catalogRoot.catalog.itemCount

    return (
      <FlexLayoutReportNavigatorStoreContext.Provider value={store}>
        <VipFlexLayoutTabStoreContext.Provider value={tabMenuStore}>
          <div className={classNames(css.root, className)} data-type='FlexLayoutReportNavigator'>
            <PrimaryIconButton tooltip='First Tab' tooltipPosition='bottom' icon='double-chevron-left' onClick={goStart}/>
            <PrimaryIconButton tooltip='Previous Tab' tooltipPosition='bottom' icon='chevron-left' onClick={goPrevious}/>
            <Popover2
              // isOpen={isPopoverOpen}
              // onInteraction={onPopoverInteraction}
              interactionKind='hover'
              minimal
              placement='bottom-start'
              content={<FlexLayoutReportNavigatorMenu/>}>
              <Button className={css.dropdown}
                      onClick={togglePopover}
                      title={activeTab?.displayNameLong}
                      onDoubleClick={toggleMaximize}
                      minimal
                //icon={activeTab ? <span className={css.pageNumber}>{activeTab.pageNumber}</span> : null}
                // text={false && <div className={css.nameAndCount}>
                //   <span className={css.name}>{activeTab?.displayNameLong}</span>
                //   {!_.isUndefined(count) && <span className={css.count}>({count})</span>}
                // </div>}
                      text={<div className={css.nameAndCount}>
                        <span className={css.name}>{activeTab?.displayNameLong}</span>
                        <span className={css.count}>{activeTab?.pageNumber ? `${activeTab?.pageNumber} / ${tabsInOrder.length}` : ''}</span>
                      </div>}
                      rightIcon='caret-down'/>
            </Popover2>
            <PrimaryIconButton tooltip='Next Tab' tooltipPosition='bottom' icon='chevron-right' onClick={goNext}/>
            <PrimaryIconButton tooltip='Last Tab' tooltipPosition='bottom' icon='double-chevron-right' onClick={goEnd}/>

            {/*<Popover2*/}
            {/*  interactionKind='hover'*/}
            {/*  minimal*/}
            {/*  placement='bottom-start'*/}
            {/*  content={*/}
            {/*    <Menu>*/}
            {/*      */}
            {/*    </Menu>*/}
            {/*  }>*/}
            {/*  <Button minimal rightIcon='plus' text='Add Page...'/>*/}
            {/*</Popover2>*/}
          </div>
        </VipFlexLayoutTabStoreContext.Provider>
      </FlexLayoutReportNavigatorStoreContext.Provider>
    )
  }
})

export const FlexLayoutCurrentReportMenuItems = observer(function FlexLayoutCurrentReportMenuItems() {
  const {flexLayout: {tabsInOrder}} = useStores()

  return (<Fragment>
    {tabsInOrder.map((tab, i) => <FlexLayoutReportMenuItem tab={tab} pageNumber={i + 1} key={tab.id}/>)}
  </Fragment>)
})

const FlexLayoutReportMenuItem = observer<{ tab: TabNode, pageNumber: number }>(function FlexLayoutReportMenuItem({tab, pageNumber}) {
  const {flexLayout: {setActiveTab, activeCatalogRoot, activeTab}} = useStores(),
    {catalogRoot} = tab,
    count = !catalogRoot ? undefined : catalogRoot.catalog.itemCount

  return <MenuItem className={css.tabPageMenuItem}
                   text={<div className={css.nameAndCount}>
                     <span className={css.name}>{tab.displayNameLong}</span>
                     {!_.isUndefined(count) && <span className={css.count}>({count})</span>}
                   </div>}
                   active={tab === activeTab}
                   data-id={tab?.id}
                   shouldDismissPopover={false}
                   data-is-active-catalog={activeCatalogRoot?.tab === tab}
                   labelElement={
                     <div className={css.tabRight}>
                       <MyFlexLayoutTabContentLeading node={tab}/>
                       <PrimaryIconButton tooltip='Maximize' icon={'maximize'} active={tab.isMaximized} onClick={tab.toggleMaximize} tooltipPosition='right'/>
                       <PrimaryIconButton tooltip='Delete Page' icon={'trash'} onClick={tab._delete} tooltipPosition='right'/>
                     </div>}
                   icon={<span className={css.pageNumber}>{pageNumber}</span>}
                   onClick={() => setActiveTab(tab)}/>
})

export const FlexLayoutReportNavigatorMenu = observer(function FlexLayoutReportNavigatorMenu() {
    const {flexLayout: {activeTab}} = useStores()

    return (<React.Fragment>
      <Menu className={css.menu}>
        <ContextMenuItem icon='new-layer' text='Add Page...'>
          <NewFlexTabMenuItems tab={activeTab}/>
        </ContextMenuItem>
        <MenuDivider/>
        <FlexLayoutCurrentReportMenuItems/>
      </Menu>
    </React.Fragment>)
})


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/FlexLayoutReportNavigator.tsx